<template>
  <div class="upload-crop-image">
    <el-upload
      action=""
      list-type="picture-card"
      :class="{ 'is-hide-trigger': !!innerUrl || disabled }"
      :file-list="fileList"
      :limit="1"
      :accept="accept"
      :disabled="disabled"
      :before-upload="handleBeforeUpload"
      :on-remove="handleRemove"
      :on-exceed="handleExceed"
    >
      <i class="el-icon-plus"></i>
      <div slot="file" slot-scope="{ file }" class="upload-crop-image__card">
        <el-image
          ref="previewImage"
          class="upload-crop-image__thumb"
          :src="file.url"
          fit="contain"
          :preview-src-list="[file.url]"
        />
        <div class="upload-crop-image__mask" @click.stop>
          <span class="upload-crop-image__action" @click="openPreview">
            <i class="el-icon-zoom-in" />
          </span>
          <span
            v-if="!disabled"
            class="upload-crop-image__action"
            @click="handleRemove"
          >
            <i class="el-icon-delete" />
          </span>
        </div>
      </div>
    </el-upload>
    <div v-if="tipText" class="upload-crop-image__tip">{{ tipText }}</div>

    <Dialog
      title="图片尺寸裁剪"
      :visible.sync="cropVisible"
      width="1200px"
      :close-on-click-modal="false"
      :append-to-body="true"
      :before-close="closeCrop"
      @opened="initCropper"
    >
      <div class="upload-crop-image__cropper">
        <img ref="cropImg" :src="cropSrc" alt="" />
      </div>
      <div class="upload-crop-image__crop-hint">
        裁剪比例 {{ aspectLabel }}，可拖动/缩放选区后确认上传
      </div>
      <div slot="footer">
        <el-button plain round size="small" @click="closeCrop">取 消</el-button>
        <el-button
          plain
          round
          size="small"
          type="primary"
          :loading="uploading"
          @click="confirmCrop"
          >确认上传</el-button
        >
      </div>
    </Dialog>
  </div>
</template>

<script>
import Cropper from "cropperjs";
import "cropperjs/dist/cropper.css";
import { uploadFile } from "@/utils/alioss.js";

export default {
  name: "UploadCropImage",
  props: {
    /** v-model 图片 URL */
    value: {
      type: String,
      default: ""
    },
    /**
     * 裁剪比例：数字（宽/高）或字符串 "16:9"
     * 默认 16:9
     */
    aspectRatio: {
      type: [Number, String],
      default: "16:9"
    },
    /** 导出宽度（px），高度按比例计算；也可同时传 outputHeight */
    outputWidth: {
      type: Number,
      default: 800
    },
    outputHeight: {
      type: Number,
      default: 0
    },
    /** 最大原图大小 MB */
    maxSize: {
      type: Number,
      default: 5
    },
    accept: {
      type: String,
      default: "image/jpeg,image/png,image/jpg,image/webp"
    },
    fileTypes: {
      type: Array,
      default: () => ["jpg", "jpeg", "png", "webp"]
    },
    /** 导出 MIME，默认 jpeg */
    outputType: {
      type: String,
      default: "image/jpeg"
    },
    /** jpeg 质量 0~1 */
    quality: {
      type: Number,
      default: 0.92
    },
    disabled: {
      type: Boolean,
      default: false
    },
    tip: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      innerUrl: "",
      cropVisible: false,
      cropSrc: "",
      cropper: null,
      uploading: false,
      rawFileName: "cover.jpg"
    };
  },
  computed: {
    fileList() {
      if (!this.innerUrl) return [];
      return [{ name: this.rawFileName, url: this.innerUrl }];
    },
    ratioNumber() {
      if (
        typeof this.aspectRatio === "string" &&
        this.aspectRatio.includes(":")
      ) {
        const parts = this.aspectRatio.split(":");
        const w = Number(parts[0]);
        const h = Number(parts[1]);
        if (w > 0 && h > 0) return w / h;
      }
      const n = Number(this.aspectRatio);
      return n > 0 ? n : 16 / 9;
    },
    aspectLabel() {
      if (
        typeof this.aspectRatio === "string" &&
        this.aspectRatio.includes(":")
      ) {
        return this.aspectRatio;
      }
      return this.ratioNumber.toFixed(2);
    },
    tipText() {
      if (this.tip) return this.tip;
      return `建议比例 ${this.aspectLabel}，上传后将按该比例裁剪`;
    },
    exportSize() {
      const width = this.outputWidth > 0 ? this.outputWidth : 800;
      let height = this.outputHeight;
      if (!height || height <= 0) {
        height = Math.round(width / this.ratioNumber);
      }
      return { width, height };
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        this.innerUrl = val || "";
      }
    }
  },
  beforeDestroy() {
    this.destroyCropper();
    this.revokeCropSrc();
  },
  methods: {
    emitValue(url) {
      this.innerUrl = url || "";
      this.$emit("input", this.innerUrl);
      this.$emit("change", this.innerUrl);
    },
    handleExceed() {
      this.$message.warning("最多只能上传 1 张图片");
    },
    openPreview() {
      const image = this.$refs.previewImage;
      if (image && typeof image.clickHandler === "function") {
        image.clickHandler();
      }
    },
    handleRemove() {
      this.emitValue("");
      this.$emit("remove");
    },
    handleBeforeUpload(file) {
      if (this.disabled) return false;
      const suffix = (file.name.split(".").pop() || "").toLowerCase();
      if (this.fileTypes.length && !this.fileTypes.includes(suffix)) {
        this.$message.error(`仅支持 ${this.fileTypes.join("、")} 格式`);
        return false;
      }
      if (!file.type || !file.type.startsWith("image/")) {
        this.$message.error("仅支持图片文件");
        return false;
      }
      if (file.size / 1024 / 1024 >= this.maxSize) {
        this.$message.error(`上传图片大小不能超过 ${this.maxSize}M`);
        return false;
      }
      this.rawFileName = file.name || "cover.jpg";
      this.revokeCropSrc();
      this.destroyCropper();
      this.cropSrc = URL.createObjectURL(file);
      this.cropVisible = true;
      return false;
    },
    initCropper() {
      this.destroyCropper();
      const img = this.$refs.cropImg;
      if (!img || !this.cropSrc) return;
      this.cropper = new Cropper(img, {
        aspectRatio: this.ratioNumber,
        viewMode: 1,
        dragMode: "move",
        autoCropArea: 1,
        responsive: true,
        background: false,
        guides: true,
        center: true,
        highlight: false,
        cropBoxMovable: true,
        cropBoxResizable: true,
        toggleDragModeOnDblclick: false
      });
    },
    destroyCropper() {
      if (this.cropper) {
        this.cropper.destroy();
        this.cropper = null;
      }
    },
    revokeCropSrc() {
      if (this.cropSrc && this.cropSrc.startsWith("blob:")) {
        URL.revokeObjectURL(this.cropSrc);
      }
      this.cropSrc = "";
    },
    closeCrop(done) {
      this.destroyCropper();
      this.revokeCropSrc();
      this.cropVisible = false;
      if (typeof done === "function") done();
    },
    canvasToFile(canvas) {
      const type = this.outputType || "image/jpeg";
      const quality = this.quality;
      const baseName = this.rawFileName.replace(/\.[^.]+$/, "") || "cover";
      const ext = type === "image/png" ? "png" : "jpg";
      const fileName = `${baseName}_crop.${ext}`;
      return new Promise((resolve, reject) => {
        if (canvas.toBlob) {
          canvas.toBlob(
            blob => {
              if (!blob) {
                reject(new Error("裁剪失败"));
                return;
              }
              resolve(
                new File([blob], fileName, {
                  type,
                  lastModified: Date.now()
                })
              );
            },
            type,
            quality
          );
        } else {
          try {
            const dataUrl = canvas.toDataURL(type, quality);
            const arr = dataUrl.split(",");
            const mime = arr[0].match(/:(.*?);/)[1];
            const bstr = atob(arr[1]);
            let n = bstr.length;
            const u8arr = new Uint8Array(n);
            while (n--) u8arr[n] = bstr.charCodeAt(n);
            resolve(
              new File([u8arr], fileName, {
                type: mime,
                lastModified: Date.now()
              })
            );
          } catch (e) {
            reject(e);
          }
        }
      });
    },
    async confirmCrop() {
      if (!this.cropper || this.uploading) return;
      this.uploading = true;
      try {
        const { width, height } = this.exportSize;
        const canvas = this.cropper.getCroppedCanvas({
          width,
          height,
          imageSmoothingEnabled: true,
          imageSmoothingQuality: "high"
        });
        if (!canvas) {
          this.$message.error("裁剪失败，请重试");
          return;
        }
        const file = await this.canvasToFile(canvas);
        const res = await uploadFile("image", file, file.name);
        if (res && res.res && res.res.status === 200) {
          const url = res.res.requestUrls[0];
          this.emitValue(url);
          this.$emit("success", { url, fileName: file.name });
          this.closeCrop();
        } else {
          this.$message.error("上传失败");
        }
      } catch (e) {
        this.$message.error("上传失败");
      } finally {
        this.uploading = false;
      }
    }
  }
};
</script>

<style scoped lang="less">
.upload-crop-image {
  /deep/ .is-hide-trigger {
    .el-upload--picture-card {
      display: none;
    }
  }

  /deep/ .el-upload-list--picture-card .el-upload-list__item {
    overflow: hidden;
  }

  &__card {
    position: relative;
    width: 100%;
    height: 100%;
  }

  &__thumb {
    width: 100%;
    height: 100%;
    display: block;
    background: #f5f7fa;

    /deep/ .el-image__inner {
      object-fit: contain;
    }
  }

  &__mask {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    color: #fff;
    opacity: 0;
    background: rgba(0, 0, 0, 0.45);
    transition: opacity 0.2s;
  }

  &__card:hover &__mask {
    opacity: 1;
  }

  &__action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin: 0 8px;
    font-size: 20px;
    cursor: pointer;
  }

  &__tip {
    margin-top: 8px;
    font-size: 12px;
    color: #86909c;
    line-height: 1.5;
  }

  &__cropper {
    width: 100%;
    height: 480px;
    background: #1d2129;

    /deep/ img {
      max-width: 100%;
      display: block;
    }
  }

  &__crop-hint {
    margin-top: 12px;
    font-size: 13px;
    color: #86909c;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/UploadCropImage.vue